// @flow

import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import t from 'app/utils/translate';
import { get, last, first } from 'lodash';

import {
  resetLobbyFilter,
  setLobbyFilter,
  setLobbyGroupBy,
  toggleLobbyFilter,
  setLobbyCategory,
  unsetLobbyFilter
} from 'app/actions/lobbyActions';
import lobbyFiltersSelector from 'app/selectors/lobbyFiltersSelector';
import {
  COMPLEXITY,
  MAX_ENTRY_CAP,
  MAX_FEE,
  MIN_ENTRY_CAP,
  MIN_FEE,
  PAYOUT,
  PLAYERS
} from 'app/constants/filterTypes';
import { CUSTOM, FEATURED, FREE, PAID, H2H, ALL } from 'app/constants/challengeCategoryTypes';
import { LINE_UP_SMALL, LINE_UP_MEDIUM, LINE_UP_LARGE } from 'app/constants/lineUpSizeTypes';
import { MULTIPLIER } from 'app/constants/payoutTypes';
import { DATE } from 'app/constants/groupByTypes';
import ChallengesFilterBar from './ChallengesFilterBar';

function mapStateToProps(state) {
  return {
    challengeCategories: get(state.settings, 'challengeCategories'),
    displayComplexityFilter: state.settings.filters.complexity,
    displayEntrantsFilter: state.settings.filters.entrants,
    displayFeeFilter: state.settings.filters.fee,
    displayPayoutFilter: state.settings.filters.payout,
    displaySizeFilter: state.settings.filters.size,
    filters: lobbyFiltersSelector(state),
    groupByKey: state.lobbyChallenges.groupByKey,
    hasCoupon: get(state.user, 'currentUser.has_coupon', false),
    isFreeOnly: state.settings.freeOnly,
    activeCategory: state.lobbyChallenges.activeCategory,
    entrantsOptions: state.settings.filters.entrantsOptions,
    feeOptions: state.settings.filters.feeOptions
  };
}

const actions = {
  onComplexityChange(value) {
    return toggleLobbyFilter(COMPLEXITY, value);
  },
  onCategoryChange(value) {
    return setLobbyCategory(value);
  },
  onGroupByDateChange(value) {
    return value ? setLobbyGroupBy(DATE) : setLobbyGroupBy(null);
  },
  onLineUpSizeChange(value) {
    return toggleLobbyFilter(PLAYERS, value);
  },
  onMaxEntrantsChange(value) {
    return !isNil(value) ? setLobbyFilter(MAX_ENTRY_CAP, value) : unsetLobbyFilter(MAX_ENTRY_CAP);
  },
  onMaxFeeChange(value) {
    return !isNil(value) ? setLobbyFilter(MAX_FEE, value) : unsetLobbyFilter(MAX_FEE);
  },
  onMinEntrantsChange(value) {
    return !isNil(value) ? setLobbyFilter(MIN_ENTRY_CAP, value) : unsetLobbyFilter(MIN_ENTRY_CAP);
  },
  onMinFeeChange(value) {
    return !isNil(value) ? setLobbyFilter(MIN_FEE, value) : unsetLobbyFilter(MIN_FEE);
  },
  onPayoutChange(value) {
    return toggleLobbyFilter(PAYOUT, value);
  },
  onReset() {
    return resetLobbyFilter();
  }
};

function mergeProps(stateProps, dispatchProps, ownProps) {
  const {
    activeCategory,
    challengeCategories,
    filters,
    groupByKey,
    hasCoupon,
    isFreeOnly,
    ...restStateProps
  } = stateProps;

  let categories = [
    {
      id: ALL,
      name: t('All')
    },
    {
      id: FEATURED,
      name: t('Featured')
    },
    {
      id: PAID,
      name: isFreeOnly ? t('Platinum') : t('Paid')
    },
    {
      id: FREE,
      name: t('Free')
    },
    {
      id: H2H,
      name: 'Head to Head',
      shortName: 'H2H'
    },
    {
      id: CUSTOM,
      name: t('Custom'),
      icon: 'user'
    }
  ];

  if (challengeCategories) {
    categories = categories.filter(cat => challengeCategories.includes(cat.id));
  }

  const lineUpSizes = [
    {
      id: LINE_UP_SMALL,
      label: '1 - 5',
      checked: filters[PLAYERS] && filters[PLAYERS].includes(LINE_UP_SMALL),
      onChange() {
        dispatchProps.onLineUpSizeChange(LINE_UP_SMALL);
      }
    },
    {
      id: LINE_UP_MEDIUM,
      label: '6 - 10',
      checked: filters[PLAYERS] && filters[PLAYERS].includes(LINE_UP_MEDIUM),
      onChange() {
        dispatchProps.onLineUpSizeChange(LINE_UP_MEDIUM);
      }
    },
    {
      id: LINE_UP_LARGE,
      label: '11+',
      checked: filters[PLAYERS] && filters[PLAYERS].includes(LINE_UP_LARGE),
      onChange() {
        dispatchProps.onLineUpSizeChange(LINE_UP_LARGE);
      }
    }
  ];

  const payouts = [
    {
      id: 'guaranteed',
      label: 'Guaranteed',
      value: 'guaranteed',
      checked: filters[PAYOUT] && filters[PAYOUT].includes('guaranteed'),
      onChange() {
        dispatchProps.onPayoutChange('guaranteed');
      }
    },
    {
      id: MULTIPLIER,
      label: 'Multiplier',
      value: MULTIPLIER,
      checked: filters[PAYOUT] && filters[PAYOUT].includes(MULTIPLIER),
      onChange() {
        dispatchProps.onPayoutChange(MULTIPLIER);
      }
    }
  ];

  return {
    ...ownProps,
    ...dispatchProps,
    ...restStateProps,
    activeCategory,
    categories,
    complexity: filters[COMPLEXITY],
    groupByDate: groupByKey === DATE,
    isFiltered: !isEmpty(filters),
    lineUpSizes,
    maxEntrants: filters[MAX_ENTRY_CAP],
    maxFee: get(filters, MAX_FEE, last(restStateProps.feeOptions)),
    minEntrants: filters[MIN_ENTRY_CAP],
    minFee: get(filters, MIN_FEE, first(restStateProps.feeOptions)),
    payouts
  };
}

export default connect(mapStateToProps, actions, mergeProps)(ChallengesFilterBar);
